/**
 * Favoritos de conversa do usuário na organização atual.
 * Sync multi-device via PartyKit (`favorite:changed` com source partykit).
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { notify } from '../../../utils/toast';

function emitFavoriteChanged(
    conversationId: string,
    userId: string,
    action: 'INSERT' | 'DELETE',
    organizationId: string,
) {
    window.dispatchEvent(new CustomEvent('favorite:changed', {
        detail: { conversationId, userId, action, organizationId },
    }));
}

export const useChatFavorites = () => {
    const { user } = useAuth();
    const { currentOrganization } = useOrganization();
    const userId = user?.id;
    const orgId = currentOrganization?.id;
    const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set());

    const loadFavorites = useCallback(async () => {
        if (!userId || !orgId) return;
        try {
            const { data, error } = await supabase
                .from('chat_favorites')
                .select('conversation_id')
                .eq('user_id', userId)
                .eq('organization_id', orgId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setFavoriteIds(new Set((data ?? []).map((row) => row.conversation_id)));
        } catch (error) {
            console.error('[useChatFavorites] Erro ao carregar favoritos:', error);
        }
    }, [userId, orgId]);

    useEffect(() => {
        void loadFavorites();
    }, [loadFavorites]);

    useEffect(() => {
        if (!userId) return;
        const onFavorite = (event: Event) => {
            const detail = (event as CustomEvent<{ userId?: string; source?: string }>).detail;
            if (detail?.source !== 'partykit') return;
            if (detail.userId && detail.userId !== userId) return;
            void loadFavorites();
        };
        window.addEventListener('favorite:changed', onFavorite);
        return () => window.removeEventListener('favorite:changed', onFavorite);
    }, [userId, loadFavorites]);

    const isFavorite = useCallback(
        (conversationId: string) => favoriteIds.has(conversationId),
        [favoriteIds],
    );

    const addFavorite = useCallback(async (conversationId: string) => {
        if (!userId || !orgId) return false;
        try {
            const { error } = await supabase.from('chat_favorites').insert({
                user_id: userId,
                conversation_id: conversationId,
                organization_id: orgId,
            });
            if (error) {
                if (error.code === '23505') return true;
                throw error;
            }
            setFavoriteIds((prev) => new Set(prev).add(conversationId));
            notify.success('Conversa adicionada aos favoritos');
            emitFavoriteChanged(conversationId, userId, 'INSERT', orgId);
            return true;
        } catch (error) {
            console.error('[useChatFavorites] Erro ao adicionar favorito:', error);
            notify.error('Erro ao favoritar conversa');
            return false;
        }
    }, [userId, orgId]);

    const removeFavorite = useCallback(async (conversationId: string) => {
        if (!userId) return false;
        try {
            const { error } = await supabase
                .from('chat_favorites')
                .delete()
                .eq('user_id', userId)
                .eq('conversation_id', conversationId);
            if (error) throw error;
            setFavoriteIds((prev) => {
                const next = new Set(prev);
                next.delete(conversationId);
                return next;
            });
            notify.success('Conversa removida dos favoritos');
            if (orgId) emitFavoriteChanged(conversationId, userId, 'DELETE', orgId);
            return true;
        } catch (error) {
            console.error('[useChatFavorites] Erro ao remover favorito:', error);
            notify.error('Erro ao remover favorito');
            return false;
        }
    }, [userId, orgId]);

    const toggleFavorite = useCallback(async (conversationId: string) => (
        isFavorite(conversationId) ? removeFavorite(conversationId) : addFavorite(conversationId)
    ), [isFavorite, addFavorite, removeFavorite]);

    const checkAndAutoUnfavorite = useCallback(async (conversationId: string) => (
        currentOrganization?.role === 'member' ? removeFavorite(conversationId) : false
    ), [currentOrganization?.role, removeFavorite]);

    return { favoriteIds, isFavorite, toggleFavorite, checkAndAutoUnfavorite };
};
